import { MB_customWindowForWeb } from '@/utils/general/custom-window-for-web';
import { isMobile, isMobileApp } from '@/utils/general/device-info';
import { MB_fileManagement } from '@/utils/general/file-management';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import { ActivityIndicator, Platform, StyleSheet, Text, TouchableOpacity, View, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { MB_resourcePicker } from './MB_resourcePicker';
import { MB_ResourcePickerCameraOrGalleryPopUp, MB_RESOURCE_PICKER_TYPE_CALLBACK } from './MB_ResourcePickerCameraOrGalleryPopUp';
import { COLORS, MB_RESOURCE_PICKER_CONSTANTS, STRING_CONSTANTS } from './MB_ResourcePickerConstants';
import { MB_ResourcePickerDropzoneForWeb, MB_ResourcePickerDropzoneForWebRef } from './MB_ResourcePickerDropzoneForWeb';
import { MB_ResourcePickerResource, MB_RESOURCE_PICKER_TYPE } from './MB_ResourcePickerUtils';
import * as mime from '@/utils/general/mime-types';
import { MB_imageCompressor } from '@/utils/general/image-compressor';
import { mbApplyTransparency, mbPlatformStyle } from '@/utils/general/style-utils';
import { MB_ResourcePickerAndUploaderPreview } from './MB_ResourcePickerAndUploaderPreview';
import { MB_SVG_Image, MB_SVG_Video, MB_SVG_File } from '@/components/general/svg-components';
import { MB_ImageCropperPopUpView } from '@/components/general/image-cropper';
import { MB_ModalProgressCircleBody } from '@/components/general/modal-progress-circle';
import { MB_utilHooks } from '@/utils/general/util-hooks';
import { MB_Modal } from '@/components/general/modal';
import { Linking } from 'react-native';
import { MB_Button } from '@/components/general/button';

interface MB_ResourcePickerUIStyleProps {
  /**
   * Style of the container.
   *
   * Note: this style will be applied to already predefined styles instead of
   * replacing them.
   */
  containerStyle?: StyleProp<ViewStyle>;

  /**
   * Style of the preview image background.
   */
  previewImageBackgroundStyle?: StyleProp<ViewStyle>;

  /**
   * Style of the drag and drop text.
   */
  dragAndDropAndProcessingResourceButtonColor?: string;

  /**
   * TODO: Add description
   */
  previewContainerTextColor?: string;

  dragAndDropAndProcessingResourceTextStyle?: StyleProp<TextStyle>;

  /**
   * Style of the camera or gallery modal's children.
   */
  cameraOrGalleryModalChildrenStyle?: StyleProp<ViewStyle>;

  /**
   * Style of the camera or gallery button.
   */
  cameraOrGalleryButtonStyle?: StyleProp<ViewStyle>;

  /**
   * Style of the camera or gallery text.
   */
  cameraorGalleryTextStyle?: StyleProp<TextStyle>;

  /**
   * Style of the camera or gallery cancel button.
   */
  cameraOrGalleryCancelButtonStyle?: StyleProp<ViewStyle>;
  modalTitleWrapperStyle?: StyleProp<ViewStyle>;
  wrapperStyle?: StyleProp<ViewStyle>;
  modalTitleStyle?: StyleProp<TextStyle>;
  uploadButtonStyle?: StyleProp<ViewStyle>;
  uploadButtonTextStyle?: StyleProp<TextStyle>;
}

interface MB_ResourcePickerUIProps extends MB_ResourcePickerUIStyleProps {
  /**
   * The type of the resource picker.
   */
  resourcePickerType: MB_RESOURCE_PICKER_TYPE;

  /**
   * The picked resource.
   */
  resource: MB_ResourcePickerResource | undefined;

  /**
   * Callback to call when the resource changes.
   */
  onResourceChanged: (newResource: MB_ResourcePickerResource | undefined) => void;

  /**
   * The upload progress percentage.
   */
  uploadPercentage: number;

  /**
   * Whether the resource is loading.
   */
  isLoading: boolean;

  /**
   * Callback to call when an error occurs.
   */
  setErrorMessage: (message: string) => void;

  /**
   * The allowed file extensions. In format `'.jpeg,.jpg,.png'`.
   */
  allowedFileExtensions?: string;

  /**
   * This is used when picker type is anyFile. Expected to be an array of mime types such as
   * 'image/*', 'image/png', 'application/pdf'.
   */
  allowedDocumentPickerTypes?: string[];

  /**
   * The color of the camera or gallery icon.
   */
  cameraorGalleryIconColor?: string;

  /**
   * Controls the color and size for the close button's X symbol.
   */
  cameraOrGalleryModalCloseButtonXProps?: {
    /**
     * The size of the X symbol.
     */
    size?: number;

    /**
     * The color of the X symbol.
     */
    color?: string;
  };

  /**
   * The color of the preview.
   */
  previewColor?: string;

  /**
   * The aspect ratio to crop the image to.
   */
  cropAspectRatio?: [number, number];

  /**
   * The maximum duration of the video in seconds.
   */
  maxDuration?: number;
}

function videoFileIsTooLargeText(maxVideoSize: number) {
  return 'Video file is too large.\nPlease try a video file less than ' + maxVideoSize / (1024 * 1024) + ' MB.';
}

function fileIsTooLargeText(maxFileSize: number) {
  return 'File is too large.\nPlease try an image less than ' + maxFileSize / (1024 * 1024) + ' MB.';
}

interface PermissionDeniedModalProps {
  /**
   * Whether the modal is visible.
   */
  isVisible: boolean;

  /**
   * Style of the modal's children.
   */
  modalChildrenStyle?: StyleProp<ViewStyle>;

  /**
   * Controls the color and size for the modal close button's X symbol.
   */
  modalCloseButtonXProps?: {
    /**
     * The size of the X symbol.
     */
    size?: number;

    /**
     * The color of the X symbol.
     */
    color?: string;
  };

  /**
   * Style of the button.
   */
  buttonStyle?: StyleProp<ViewStyle>;

  /**
   * Callback to call when the modal is dismissed.
   */
  onDismiss?: () => void;
  modalTitleWrapperStyle?: StyleProp<ViewStyle>;
  modalTitleStyle?: StyleProp<TextStyle>;
}

const PermissionDeniedModal = ({
  isVisible,
  modalChildrenStyle,
  modalCloseButtonXProps,
  buttonStyle,
  onDismiss,
  modalTitleWrapperStyle,
  modalTitleStyle,
}: PermissionDeniedModalProps) => {
  return (
    <MB_Modal
      isVisible={isVisible}
      onDismiss={onDismiss}
      closeButtonXProps={modalCloseButtonXProps}
      childrenWrapperStyle={modalChildrenStyle}
      titleWrapperStyle={modalTitleWrapperStyle}
      titleStyle={modalTitleStyle}
    >
      <Text style={styles.permissionDeniedTitle}>Photo permissions</Text>
      <Text style={styles.goToSettingsText}>Go to your settings to allow W.U. have access to all your photos.</Text>
      <MB_Button title="Go to settings" style={[styles.goToSettingButton, buttonStyle]} onPress={Linking.openSettings} />
    </MB_Modal>
  );
};

const MB_ResourcePickerUISetDefaultStyles = (props: MB_ResourcePickerUIStyleProps) => {
  defaultProps = props;
};

let defaultProps: MB_ResourcePickerUIStyleProps | undefined;

const MB_ResourcePickerUIInternal = ({
  resourcePickerType,
  resource,
  onResourceChanged,
  uploadPercentage,
  isLoading,
  setErrorMessage,
  containerStyle,
  previewImageBackgroundStyle,
  dragAndDropAndProcessingResourceButtonColor,
  allowedFileExtensions,
  allowedDocumentPickerTypes,
  dragAndDropAndProcessingResourceTextStyle,
  cameraOrGalleryModalChildrenStyle,
  cameraOrGalleryModalCloseButtonXProps,
  cameraOrGalleryButtonStyle,
  cameraorGalleryTextStyle,
  cameraorGalleryIconColor,
  cameraOrGalleryCancelButtonStyle,
  previewColor,
  cropAspectRatio,
  wrapperStyle,
  modalTitleWrapperStyle,
  modalTitleStyle,
  uploadButtonStyle,
  uploadButtonTextStyle,
  maxDuration = 60,
}: MB_ResourcePickerUIProps) => {
  const [isResourcePickerTypeVisible, showResourcePickerType, hideResourcePickerType] = MB_utilHooks.useBool();
  const [isPermissionDeniedModalVisible, showPermissionDeniedModal, hidePermissionDeniedModal] = MB_utilHooks.useBool();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cropperData, setCropperData] = useState<{ imageBlob?: Blob; isVisible: boolean }>({ isVisible: false });
  const [processProgress, setProcessProgress] = useState<number | undefined>();

  const dropzoneRef = useRef<MB_ResourcePickerDropzoneForWebRef>(null);

  const onCropResultReady = useCallback(
    ({ croppedImage, url }: { croppedImage: Blob | undefined; url: string }) => {
      try {
        setIsProcessing(true);

        setCropperData({ isVisible: false, imageBlob: undefined });
        onResourceChanged(undefined);

        const type = croppedImage?.type ?? mime.lookup(url);

        const withBrowserImageCompression = !(type === 'image/jpeg' || type === 'image/jpg' || type === 'image/jfif');
        let compressPromise = MB_imageCompressor({
          blobOrUrl: isMobileApp ? url : croppedImage,
          onProgress: setProcessProgress,
          maxSizeAfterCompression: MB_RESOURCE_PICKER_CONSTANTS.MAX_IMAGE_SIZE_AFTER_COMPRESSION,
          withBrowserImageCompression,
          resultPathMobile: MB_fileManagement.getDirs().CacheDir + '/MB_ImageCompressor',
        });

        compressPromise
          .then(result => {
            onResourceChanged(result);
          })
          .catch(error => {
            if (error === fileIsTooLargeText(MB_RESOURCE_PICKER_CONSTANTS.MAX_IMAGE_SIZE_AFTER_COMPRESSION)) {
              setErrorMessage(fileIsTooLargeText(MB_RESOURCE_PICKER_CONSTANTS.MAX_IMAGE_SIZE_AFTER_COMPRESSION));
            } else {
              console.error('Error when compressing the image in resource picker. ', error);
              setErrorMessage(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
            }
          })
          .finally(async () => {
            if (isMobileApp) {
              //// TODO: Mobile: Do we want to keep these?
              //await MB_fileManagement.deleteFile(url);
            }

            setIsProcessing(false);
          });
      } catch (error) {
        setIsProcessing(false);
        setErrorMessage(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
        console.error('Error when trying to compress image after cropper', error);
      }
    },
    [onResourceChanged, setErrorMessage]
  );

  const processResource = useCallback(
    async ({ resourceData, videoDuration }: { resourceData: MB_ResourcePickerResource; videoDuration?: number }) => {
      try {
        if (resourcePickerType === MB_RESOURCE_PICKER_TYPE.image) {
          onResourceChanged(undefined);
          if (isMobileApp) {
            onCropResultReady({ croppedImage: resourceData.blob, url: resourceData.uri });
          } else {
            setCropperData({ isVisible: true, imageBlob: resourceData.blob });
          }
        } else if (resourcePickerType === MB_RESOURCE_PICKER_TYPE.video) {
          const fileSize = await MB_fileManagement.getFileSize(resourceData.blob ?? resourceData.uri);

          if (fileSize > MB_RESOURCE_PICKER_CONSTANTS.MAX_VIDEO_SIZE) {
            setErrorMessage(videoFileIsTooLargeText(MB_RESOURCE_PICKER_CONSTANTS.MAX_VIDEO_SIZE));
            return;
          }

          setIsProcessing(true);

          if (isMobileApp) {
            setIsProcessing(false);
            if (videoDuration && Math.floor(videoDuration / 1000) > maxDuration) {
              setErrorMessage(STRING_CONSTANTS.VIDEO_FILE_TOO_LONG(maxDuration / 60));
              return;
            }

            onResourceChanged(resourceData);

            return;
          }
          MB_customWindowForWeb.getVideoDuration(resourceData.uri)
            .then(duration => {
              if (duration > maxDuration) {
                setErrorMessage(STRING_CONSTANTS.VIDEO_FILE_TOO_LONG(maxDuration / 60));
                return;
              }

              onResourceChanged(resourceData);
            })
            .catch(error => {
              // We were unable to get the duration, just ignore it
              console.log('Error when reading video file duration', error);
              setErrorMessage(STRING_CONSTANTS.VIDEO_FILE_INCORRECT_FORMAT);
            })
            .finally(() => setIsProcessing(false));
        } else if (resourcePickerType === MB_RESOURCE_PICKER_TYPE.anyFile) {
          const fileSize = await MB_fileManagement.getFileSize(resourceData.blob ?? resourceData.uri);

          if (fileSize > MB_RESOURCE_PICKER_CONSTANTS.MAX_FILE_SIZE) {
            setErrorMessage(videoFileIsTooLargeText(MB_RESOURCE_PICKER_CONSTANTS.MAX_FILE_SIZE));
            return;
          }

          onResourceChanged(resourceData);
        }
      } catch (error) {
        setIsProcessing(false);
        setErrorMessage(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
        console.error('Error when processing resource', error);
      }
    },
    [maxDuration, onCropResultReady, onResourceChanged, resourcePickerType, setErrorMessage]
  );

  const openResourcePicker = useCallback(
    async ({ callbackType, checkForLimitedGalleryPermission }: { callbackType?: MB_RESOURCE_PICKER_TYPE_CALLBACK; checkForLimitedGalleryPermission: boolean }) => {
      try {
        let resourcePickerResult = await MB_resourcePicker({
          cropAspectRatio,
          resourcePickerType,
          chooseFromCamera: callbackType === MB_RESOURCE_PICKER_TYPE_CALLBACK.camera,
          checkForLimitedGalleryPermission,
          allowedDocumentPickerTypes,
        });

        hideResourcePickerType();

        if (resourcePickerResult.error || !resourcePickerResult.result) {
          if (resourcePickerResult.error === 'limitedGalleryPermission') {
            setTimeout(showPermissionDeniedModal, 400);
          }

          if (resourcePickerResult.error === 'User rejected permissions') {
            setTimeout(showPermissionDeniedModal, 400);
          }

          if (resourcePickerResult.error !== 'cancelled') {
            console.error('Image picker error', resourcePickerResult.error);
            setErrorMessage(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
          }

          return;
        }

        onResourceChanged(undefined);
        setErrorMessage('');

        if (resourcePickerResult.result.type === 'image' || resourcePickerResult.result.type === 'video') {
          let resourceBlob: Blob | undefined;

          if (!isMobileApp) {
            const response = await fetch(resourcePickerResult.result.uri);
            resourceBlob = await response.blob();
          }

          // resource was picked with imagePicker
          processResource({ resourceData: { blob: resourceBlob, uri: resourcePickerResult.result.uri }, videoDuration: resourcePickerResult.result.duration });
          return;
        } else if (resourcePickerResult.result.type === 'success') {
          // resource was picket with documentPicker
          resourcePickerResult.result;

          if ((resourcePickerResult.result.size ?? Infinity) > MB_RESOURCE_PICKER_CONSTANTS.MAX_FILE_SIZE) {
            setErrorMessage(videoFileIsTooLargeText(MB_RESOURCE_PICKER_CONSTANTS.MAX_FILE_SIZE));
            return;
          }

          onResourceChanged({ blob: undefined, uri: resourcePickerResult.result.uri, fileName: resourcePickerResult.result.name });
          return;
        }

        setErrorMessage(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
      } catch (error) {
        console.log('Error when opening resource picker', error);
        setErrorMessage(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
      }
    },
    [allowedDocumentPickerTypes, cropAspectRatio, hideResourcePickerType, onResourceChanged, processResource, resourcePickerType, setErrorMessage, showPermissionDeniedModal]
  );

  const showResourcePickerTypeOrOpenResourcePicker = useCallback(() => {
    if (resourcePickerType === MB_RESOURCE_PICKER_TYPE.anyFile) {
      openResourcePicker({ checkForLimitedGalleryPermission: true });
    } else {
      showResourcePickerType();
    }
  }, [openResourcePicker, resourcePickerType, showResourcePickerType]);

  const onResourcePickerTypeCallback = useCallback(
    async (callbackType: MB_RESOURCE_PICKER_TYPE_CALLBACK) => {
      switch (callbackType) {
        case MB_RESOURCE_PICKER_TYPE_CALLBACK.dismissed:
          hideResourcePickerType();
          break;
        case MB_RESOURCE_PICKER_TYPE_CALLBACK.camera:
        case MB_RESOURCE_PICKER_TYPE_CALLBACK.gallery:
          openResourcePicker({ callbackType, checkForLimitedGalleryPermission: false });
          break;
      }
    },
    [hideResourcePickerType, openResourcePicker]
  );

  const onCropperError = (error: Error) => {
    console.error('Cropper error', error);
    setCropperData({ isVisible: false, imageBlob: undefined });
    setErrorMessage(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
  };

  const DragAndDropText = useMemo(
    () => () => {
      function onDragAndDropTextPressed() {
        if (isMobileApp) {
          showResourcePickerTypeOrOpenResourcePicker();
        } else {
          dropzoneRef.current?.openFileSelector();
          // TODO: We used to use openResourcePicker for web but it seems to be broken so for now we disable the button on web
          // Seems like expo has some issues with resource picker on web (At least the current version)
          //openResourcePicker({ checkForLimitedGalleryPermission: false });
        }
      }

      function getDragAndDropText(type: MB_RESOURCE_PICKER_TYPE, shortVersion: boolean) {
        switch (type) {
          case MB_RESOURCE_PICKER_TYPE.image:
            return shortVersion ? 'Image' : 'an Image';
          case MB_RESOURCE_PICKER_TYPE.video:
            return shortVersion ? 'Video' : 'a Video';
          case MB_RESOURCE_PICKER_TYPE.anyFile:
            return shortVersion ? 'File' : 'a File';
        }
      }

      return (
        <TouchableOpacity onPress={onDragAndDropTextPressed}>
          <Text style={[styles.dragAndDropAndProcessingResourceText, dragAndDropAndProcessingResourceTextStyle]}>
            {isMobile ? (
              `Click here to upload ${getDragAndDropText(resourcePickerType, false)}`
            ) : (
              <Text>
                Drag and Drop {getDragAndDropText(resourcePickerType, true)} or
                <Text style={{ color: dragAndDropAndProcessingResourceButtonColor ?? COLORS.cyan }}> Browse</Text>
              </Text>
            )}
          </Text>
        </TouchableOpacity>
      );
    },
    [dragAndDropAndProcessingResourceButtonColor, dragAndDropAndProcessingResourceTextStyle, resourcePickerType, showResourcePickerTypeOrOpenResourcePicker]
  );

  const containerStyleFinal = useMemo(() => {
    let style = resourcePickerType === MB_RESOURCE_PICKER_TYPE.video ? styles.innerContainerForVideo : styles.innerContainerForImage;
    return [style, containerStyle];
  }, [containerStyle, resourcePickerType]);

  return (
    <View>
      <MB_ImageCropperPopUpView
        key={cropperData.isVisible.toString()}
        isVisible={cropperData.isVisible}
        onDismiss={() => setCropperData({ isVisible: false, imageBlob: undefined })}
        imageToCrop={cropperData.imageBlob}
        onResultReady={onCropResultReady}
        onError={onCropperError}
        modalChildrenWrapperStyle={wrapperStyle}
        modalTitleWrapperStyle={modalTitleWrapperStyle}
        modalTitleStyle={modalTitleStyle}
        buttonTextStyle={uploadButtonTextStyle}
        buttonStyle={uploadButtonStyle}
        modalCloseButtonXProps={cameraOrGalleryModalCloseButtonXProps}
      />

      <MB_ResourcePickerCameraOrGalleryPopUp
        isVisible={isResourcePickerTypeVisible}
        callback={onResourcePickerTypeCallback}
        modalChildrenStyle={cameraOrGalleryModalChildrenStyle}
        modalCloseButtonXProps={cameraOrGalleryModalCloseButtonXProps}
        cameraOrGalleryButtonStyle={cameraOrGalleryButtonStyle}
        cameraorGalleryTextStyle={cameraorGalleryTextStyle}
        cameraorGalleryIconColor={cameraorGalleryIconColor}
        cancelButtonStyle={cameraOrGalleryCancelButtonStyle}
      />

      <PermissionDeniedModal
        isVisible={isPermissionDeniedModalVisible}
        onDismiss={hidePermissionDeniedModal}
        modalChildrenStyle={cameraOrGalleryModalChildrenStyle}
        modalCloseButtonXProps={cameraOrGalleryModalCloseButtonXProps}
        buttonStyle={cameraOrGalleryCancelButtonStyle}
      />

      <TouchableOpacity
        activeOpacity={1}
        disabled={isMobileApp && resourcePickerType === MB_RESOURCE_PICKER_TYPE.video && resource !== undefined}
        onPress={() => (isMobileApp ? showResourcePickerTypeOrOpenResourcePicker() : undefined)}
      >
        <View style={containerStyleFinal}>
          {Platform.OS === 'web' ? (
            <MB_ResourcePickerDropzoneForWeb
              MB_Ref={dropzoneRef}
              allowOnClickOpenFiles
              allowedFileExtensions={allowedFileExtensions}
              resourcePickerType={resourcePickerType}
              onResourcePicked={({ blob, uri }) => {
                setErrorMessage('');
                processResource({ resourceData: { blob, uri } });
              }}
              onError={setErrorMessage}
            >
              {resource && (
                <MB_ResourcePickerAndUploaderPreview
                  resourceUri={resource.uri}
                  fileName={resource.fileName ?? (resource.blob as any)?.name ?? 'Failed to get file name'}
                  resourcePickerType={resourcePickerType}
                  uploadPercentage={uploadPercentage}
                  isLoading={isLoading}
                  dragAndDropTextComponent={DragAndDropText}
                  previewColor={previewColor}
                />
              )}
            </MB_ResourcePickerDropzoneForWeb>
          ) : (
            <>
              {resource && (
                <MB_ResourcePickerAndUploaderPreview
                  resourceUri={resource.uri}
                  fileName={resource.fileName ?? (resource.blob as any)?.name ?? 'Failed to get file name'}
                  resourcePickerType={resourcePickerType}
                  uploadPercentage={uploadPercentage}
                  isLoading={isLoading}
                  dragAndDropTextComponent={DragAndDropText}
                  previewColor={previewColor}
                />
              )}
            </>
          )}

          {!isProcessing && !resource && (
            <View style={{ alignItems: 'center', justifyContent: 'center' }}>
              {resourcePickerType === MB_RESOURCE_PICKER_TYPE.image && (
                <MB_SVG_Image size={33} color={COLORS.lighterGray} containerStyle={previewImageBackgroundStyle ?? styles.imageBackground} />
              )}
              {resourcePickerType === MB_RESOURCE_PICKER_TYPE.video && (
                <MB_SVG_Video size={33} color={COLORS.lighterGray} containerStyle={previewImageBackgroundStyle ?? styles.imageBackground} />
              )}
              {resourcePickerType === MB_RESOURCE_PICKER_TYPE.anyFile && (
                <MB_SVG_File size={33} color={COLORS.lighterGray} containerStyle={previewImageBackgroundStyle ?? styles.imageBackground} />
              )}
              <DragAndDropText />
            </View>
          )}

          {isProcessing && (
            <View style={{ alignItems: 'center', justifyContent: 'center' }}>
              {processProgress === undefined ? <ActivityIndicator size="large" /> : <MB_ModalProgressCircleBody progress={processProgress} />}
              <Text style={styles.dragAndDropAndProcessingResourceText}>Processing the resource{'\n'}Please wait</Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    </View>
  );
};

const MB_ResourcePickerUI = (props: MB_ResourcePickerUIProps) => {
  if (defaultProps) {
    const newProps: MB_ResourcePickerUIStyleProps = {};
    Object.keys(defaultProps).forEach(key => {
      const stylePropsKey = key as keyof MB_ResourcePickerUIStyleProps;
      if (stylePropsKey === 'dragAndDropAndProcessingResourceButtonColor' || stylePropsKey === 'previewContainerTextColor') {
        newProps[stylePropsKey] = props[stylePropsKey] ?? defaultProps![stylePropsKey];
      } else {
        newProps[stylePropsKey] = props[stylePropsKey] ? [defaultProps![stylePropsKey], props[stylePropsKey]] : defaultProps![stylePropsKey];
      }
    });

    return <MB_ResourcePickerUIInternal {...props} {...newProps} />;
  }

  return <MB_ResourcePickerUIInternal {...props} />;
};

export { MB_ResourcePickerUI, MB_ResourcePickerUISetDefaultStyles };
export type { MB_ResourcePickerUIProps, MB_ResourcePickerUIStyleProps };

const baseStyles = StyleSheet.create({
  innerContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.englishVioletAlt,
    borderWidth: 1,
    borderRadius: 15,
    borderColor: COLORS.white,
    borderStyle: 'dashed',
    marginTop: 34,
    overflow: 'hidden',
  },
});

const styles = StyleSheet.create({
  innerContainerForImage: StyleSheet.flatten([
    baseStyles.innerContainer,
    {
      aspectRatio: 1,
      width: isMobile ? '95%' : 346,
    },
  ]),
  innerContainerForVideo: StyleSheet.flatten([
    baseStyles.innerContainer,
    {
      width: isMobile ? '95%' : 482,
      ...mbPlatformStyle({
        mobile: {
          aspectRatio: 1.4,
        },
        web: {
          height: 346,
        },
      }),
    },
  ]),
  imageBackground: {
    borderRadius: 300,
    width: 82,
    height: 82,
    backgroundColor: mbApplyTransparency(COLORS.darkPurple, 0.3),
    alignItems: 'center',
    justifyContent: 'center',
  },
  dragAndDropAndProcessingResourceText: {
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.white,
    textAlign: 'center',
    marginTop: 12,
  },
  permissionDeniedTitle: {
    position: 'absolute',
    top: 16,
    left: 16,
    fontSize: 15,
    fontWeight: '800',
    color: '#000',
  },
  goToSettingsText: {
    fontSize: 12,
    color: '#555555',
    textAlign: 'center',
  },
  goToSettingButton: {
    alignSelf: 'stretch',
    maxWidth: 334,
    backgroundColor: COLORS.plum,
    marginTop: 17,
  },
});
